import React, { useContext, useEffect, useState } from "react";
import { Image, Modal, Pressable, ScrollView, StyleSheet, View, useWindowDimensions } from "react-native";
import Icon from "react-native-vector-icons/Ionicons";
import { FindifyDataContext, FindifyDataContextType } from "@/contexts/FindifyDataContext";
import { BasePage } from "../../genericPages/helpers/BasePage";
import { BottomNavigationPadding } from "../../genericPages/components/FindifyNavigation";
import { getApiFromModule } from "../../genericPages/helpers/GetContentsByModule";
import { getFeaturedImageUrl } from "../../genericPages/helpers/DetailsHelpers";
import { getTranslatedString } from "../../genericPages/helpers/FindifyTranslatedString";
import { PlaceText } from "../../genericPages/components/PlaceText";
import { PlaceColors } from "../../genericPages/helpers/PlaceColors";
import { BiscainhosAppHeader } from "../../genericPages/components/BiscainhosAppHeader";
import { useSeoTitle } from "../../genericPages/helpers/SeoTitle";
import { getContentAudioUrl } from "../../genericPages/helpers/MediaHelpers";
import { SimpleAudioPlayer } from "../../genericPages/components/SimpleAudioPlayer";
import { getWorkspaceTitle } from "../../genericPages/helpers/WorkspaceText";

const FLOOR_SLUG_REGEX = /^floor-(\d+)$/i;

// Routes disabled in the backoffice are still returned by the API. Keep them
// out of the guided visit instead of relying on the API list to hide them.
const isPublishedRoute = (route: any) => route?.published !== false
    && route?.published !== 0
    && route?.published !== '0';

const getFloorMeta = (item: any) => {
    const slug = String(item?.page_slug ?? item?.slug ?? '').trim().toLowerCase();
    const match = slug.match(FLOOR_SLUG_REGEX);
    if (!match) return null;

    return {
        slug,
        order: Number(match[1]),
        accessible: false,
    };
}

const getPointCategoryIds = (point: any): number[] => (point?.categories ?? [])
    .map((line: any) => Number(line?.category_id ?? line?.category?.category_id))
    .filter(Number.isFinite);

const findCategoryById = (categories: any[], categoryId: number): any => {
    for (const category of categories) {
        if (Number(category?.category_id) === categoryId) return category;
        const match = findCategoryById(category?.children ?? [], categoryId);
        if (match) return match;
    }
    return undefined;
}

// A route can span several floors. Resolve each point to its top-level visit
// category so the selector is built from the route's actual locations.
const findLocationCategory = (point: any, categories: any[]) => {
    for (const categoryId of getPointCategoryIds(point)) {
        for (const root of categories) {
            if (findCategoryById([root], categoryId)) return root;
        }
    }
    return undefined;
}

const findFloorCategoryForRoute = (route: any, categories: any[]) => {
    const routeFloor = getFloorMeta(route?.__routeSummary ?? route);
    if (!routeFloor) return undefined;
    return categories.find((category: any) => getFloorMeta(category)?.order === routeFloor.order);
}

const isEnglishLanguage = (language?: string) => language?.startsWith("en");
const isFrenchLanguage = (language?: string) => language?.startsWith("fr");
const localizedText = (findifyDataContext: FindifyDataContextType | null, pt: string, en: string, fr?: string) => {
    if (isFrenchLanguage(findifyDataContext?.language)) return fr ?? en;
    return isEnglishLanguage(findifyDataContext?.language) ? en : pt;
};

const getFloorTitle = (category: any, findifyDataContext: FindifyDataContextType | null) => (
    getTranslatedString(category?.name, findifyDataContext as FindifyDataContextType)
);

const getRoutePoints = (route: any) => {
    return (route?.points_of_interest ?? [])
        .slice()
        .sort((a: any, b: any) => +(a?.sequence ?? 0) - +(b?.sequence ?? 0));
}

const getPointTitle = (routePoint: any, findifyDataContext: FindifyDataContextType | null) => {
    return getTranslatedString(routePoint?.point_of_interest?.title, findifyDataContext as FindifyDataContextType)
        || getTranslatedString(routePoint?.point_of_interest?.name, findifyDataContext as FindifyDataContextType)
        || '';
}

const isCategoryBasePoint = (routePoint: any) => {
    const value = routePoint?.point_of_interest?.show_in_highlight_section;
    return value === true || value === 1 || value === '1' || value === 'true';
};

const slugifyCategoryPart = (value: string) => (
    `${value ?? ''}`
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
);

const getCategoryDisplayTitle = (category: any, findifyDataContext: FindifyDataContextType | null) => {
    const title = getTranslatedString(category?.name, findifyDataContext as FindifyDataContextType);
    const parts = `${title ?? ''}`.split('#').filter(Boolean).map((part: string) => part.trim());
    if (parts.length >= 3) return parts.slice(2).join(' - ');
    if (parts.length === 2) return parts[1];
    return title;
}

const getInfoPointCategory = (routePoint: any) => (
    routePoint?.point_of_interest?.categories?.[0]?.category
);

const getPointDisplayTitle = (routePoint: any, findifyDataContext: FindifyDataContextType | null) => {
    if (isCategoryBasePoint(routePoint)) {
        return getCategoryDisplayTitle(getInfoPointCategory(routePoint), findifyDataContext) || getPointTitle(routePoint, findifyDataContext);
    }
    return getPointTitle(routePoint, findifyDataContext);
}

const stripHtml = (html: string) => {
    return (html ?? '')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>/gi, '\n')
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/\s+\n/g, '\n')
        .replace(/\n\s+/g, '\n')
        .trim();
}

const getPointSummary = (routePoint: any, findifyDataContext: FindifyDataContextType | null) => {
    const point = routePoint?.point_of_interest;
    return stripHtml(
        getTranslatedString(point?.resume, findifyDataContext as FindifyDataContextType)
        || getTranslatedString(point?.description, findifyDataContext as FindifyDataContextType)
        || ''
    );
}

const getImagePointCoordinates = (value: unknown) => {
    const match = String(value ?? '').match(/POINT\s*\(\s*([-+]?\d*\.?\d+)\s+([-+]?\d*\.?\d+)\s*\)/i);
    if (!match) return null;

    const x = Number(match[1]);
    const y = Number(match[2]);
    return Number.isFinite(x) && Number.isFinite(y) ? { x, y } : null;
}

const VisitNavigation = ({
    route,
    currentIndex,
    routeStarted,
    nextFloorTitle,
    isLastRoute,
    findifyDataContext,
    onPrevious,
    onNext,
    onRestart,
}: {
    route: any,
    currentIndex: number,
    routeStarted: boolean,
    nextFloorTitle?: string,
    isLastRoute: boolean,
    findifyDataContext: FindifyDataContextType | null,
    onPrevious: () => void,
    onNext: () => void,
    onRestart: () => void,
}) => {
    const colors = PlaceColors(findifyDataContext);
    const points = getRoutePoints(route);
    if (points.length === 0) return null;

    const currentPoint = points[currentIndex];
    const previousPoint = routeStarted ? points[currentIndex - 1] : undefined;
    const nextPoint = routeStarted ? points[currentIndex + 1] : currentPoint;
    const shouldContinueToNextFloor = routeStarted && !!nextFloorTitle && !nextPoint;
    const nextButtonDisabled = !nextPoint && !shouldContinueToNextFloor;
    // Route metadata can include an additional floor even when it has no
    // reachable points. The terminal state must follow the actual final route.
    const visitCompleted = routeStarted && currentIndex === points.length - 1 && isLastRoute;

    return <View style={styles.visitNavigationWrap}>
        {visitCompleted ? <View style={[styles.visitCompleteCard, { backgroundColor: colors.primary.get(), borderColor: colors.secondary.get() }]}>
            <View style={[styles.visitCompleteIcon, { backgroundColor: colors.secondary.get() }]}>
                <Icon name="checkmark" size={30} color="#fff" />
            </View>
            <View style={styles.visitCompleteCopy}>
                <PlaceText style={styles.visitCompleteTitle}>{localizedText(findifyDataContext, "Chegou ao fim da visita", "You've reached the end", "Vous êtes arrivé à la fin")}</PlaceText>
                <PlaceText style={styles.visitCompleteText}>{localizedText(findifyDataContext, "Deseja voltar ao início?", "Would you like to return to the start?", "Souhaitez-vous revenir au début ?")}</PlaceText>
            </View>
            <Pressable style={[styles.visitRestartButton, { backgroundColor: colors.secondary.get() }]} onPress={onRestart}>
                <Icon name="refresh-outline" size={21} color="#fff" />
                <PlaceText style={styles.visitRestartButtonText}>{localizedText(findifyDataContext, "Início", "Start", "Début")}</PlaceText>
            </Pressable>
        </View> : null}
    <View style={styles.visitNavigation}>
        <Pressable
            disabled={!previousPoint}
            style={[styles.visitNavButton, visitCompleted ? styles.visitNavButtonFullWidth : null, { borderColor: colors.primary.withAlpha(36) }, !previousPoint ? styles.visitNavDisabled : null]}
            onPress={onPrevious}>
            <Icon name="arrow-back-outline" size={23} color={previousPoint ? colors.primary.get() : colors.primary.withAlpha(70)} />
            <View style={styles.visitNavTextBlock}>
                <PlaceText style={[styles.visitNavLabel, { color: colors.primary.get() }, !previousPoint ? { color: colors.primary.withAlpha(90) } : null]}>{localizedText(findifyDataContext, "Sala anterior", "Previous room", "Salle précédente")}</PlaceText>
                <PlaceText numberOfLines={2} style={[styles.visitNavTitle, { color: colors.text.get() }, !previousPoint ? { color: colors.text.withAlpha(110) } : null]}>
                    {previousPoint ? getPointDisplayTitle(previousPoint, findifyDataContext) : localizedText(findifyDataContext, "Início", "Start", "Début")}
                </PlaceText>
            </View>
        </Pressable>
        {!visitCompleted ? <Pressable
            disabled={nextButtonDisabled}
            style={[styles.visitNavButton, styles.visitNavButtonNext, { backgroundColor: colors.primary.get(), borderColor: colors.primary.get() }, nextButtonDisabled ? styles.visitNavDisabled : null]}
            onPress={onNext}>
            <View style={styles.visitNavTextBlock}>
                <PlaceText style={styles.visitNavLabelNext}>{routeStarted ? localizedText(findifyDataContext, "Sala seguinte", "Next room", "Salle suivante") : localizedText(findifyDataContext, "Percurso", "Route", "Parcours")}</PlaceText>
                <PlaceText numberOfLines={2} style={styles.visitNavTitleNext}>
                    {routeStarted ? (nextPoint ? getPointDisplayTitle(nextPoint, findifyDataContext) : shouldContinueToNextFloor ? `${localizedText(findifyDataContext, "Continuar para", "Continue to", "Continuer vers")} ${nextFloorTitle}` : localizedText(findifyDataContext, "Fim", "End", "Fin")) : localizedText(findifyDataContext, "Começar visita", "Start visit", "Commencer la visite")}
                </PlaceText>
            </View>
            <Icon name="arrow-forward-outline" size={25} color="#fff" />
        </Pressable> : null}
    </View>
    </View>
}

const FloorCard = ({
    category,
    route,
    currentIndex,
    routeStarted,
    showMap,
    activeRouteId,
    routeId,
    nextFloorTitle,
    isLastRoute,
    findifyDataContext,
    onPreviousPoint,
    onNextPoint,
    onRestartRoute,
    onOpenPoint,
    onOpenMap,
    onToggleMap,
    isTablet,
    isLandscapeTablet,
}: {
    category: any,
    route: any,
    currentIndex: number,
    routeStarted: boolean,
    showMap: boolean,
    activeRouteId: number,
    routeId: number,
    nextFloorTitle?: string,
    isLastRoute: boolean,
    findifyDataContext: FindifyDataContextType | null,
    onPreviousPoint: (routeId: number) => void,
    onNextPoint: (routeId: number) => void,
    onRestartRoute: (routeId: number) => void,
    onOpenPoint: (routePoint: any) => void,
    onOpenMap: (routePoint: any) => void,
    onToggleMap: (routeId: number) => void,
    isTablet?: boolean,
    isLandscapeTablet?: boolean,
}) => {
    const colors = PlaceColors(findifyDataContext);
    const [zoomOpen, setZoomOpen] = useState(false);
    const [imageLayout, setImageLayout] = useState({ width: 0, height: 0 });
    const [zoomImageLayout, setZoomImageLayout] = useState({ width: 0, height: 0 });
    const [imageSourceSize, setImageSourceSize] = useState({ width: 0, height: 0 });
    const points = getRoutePoints(route);
    const currentPoint = points[currentIndex];
    const floorImageUrl = getFeaturedImageUrl(category);
    const pointImageUrl = getFeaturedImageUrl(currentPoint?.point_of_interest);
    const shouldShowMap = !routeStarted || showMap || !pointImageUrl;
    const imageUrl = shouldShowMap ? floorImageUrl : pointImageUrl;
    const point = currentPoint?.point_of_interest;
    // The visit card represents the active POI, not its enclosing floor or
    // category.  Always show the POI title (for example, "Mosaico").
    const pointTitle = getPointTitle(currentPoint, findifyDataContext);
    const pointSummary = getPointSummary(currentPoint, findifyDataContext);
    const audioUrl = getContentAudioUrl(point);
    const imageScale = imageLayout.width > 0 && imageLayout.height > 0 && imageSourceSize.width > 0 && imageSourceSize.height > 0
        ? Math.max(imageLayout.width / imageSourceSize.width, imageLayout.height / imageSourceSize.height)
        : 0;
    const zoomImageScale = zoomImageLayout.width > 0 && zoomImageLayout.height > 0 && imageSourceSize.width > 0 && imageSourceSize.height > 0
        ? Math.min(zoomImageLayout.width / imageSourceSize.width, zoomImageLayout.height / imageSourceSize.height)
        : 0;
    const mapPoints = points
        .map((routePoint: any) => ({
            id: routePoint?.point_of_interest_id,
            coordinates: getImagePointCoordinates(routePoint?.point_of_interest?.x_y_cord),
            routePoint,
        }))
        .filter((mapPoint: any) => mapPoint.id && mapPoint.coordinates);
    const renderMapMarkers = (layout: { width: number, height: number }, scale: number, keyPrefix: string, isZoomed = false) => {
        if (!shouldShowMap || scale <= 0) return null;

        return mapPoints.map((mapPoint: any) => {
            const isCurrentPoint = routeStarted && mapPoint.id === point?.point_of_interest_id;
            const markerSize = isCurrentPoint ? 42 : 30;
            return <Pressable
                key={`${keyPrefix}-${mapPoint.id}`}
                accessibilityLabel={getPointTitle(mapPoint.routePoint, findifyDataContext)}
                onPressIn={(event: any) => {
                    event?.stopPropagation?.();
                    if (isZoomed) setZoomOpen(false);
                    onOpenPoint(mapPoint.routePoint);
                }}
                style={[styles.mapPointMarker, {
                    left: (layout.width - imageSourceSize.width * scale) / 2 + mapPoint.coordinates.x * scale - markerSize / 2,
                    top: (layout.height - imageSourceSize.height * scale) / 2 + mapPoint.coordinates.y * scale - markerSize * 0.9,
                }]}>
                <Icon name="location" size={markerSize} color={isCurrentPoint ? colors.primary.get() : "#747474"} />
            </Pressable>
        });
    };

    useEffect(() => {
        if (!imageUrl) return;
        Image.getSize(imageUrl, (width, height) => setImageSourceSize({ width, height }), () => setImageSourceSize({ width: 0, height: 0 }));
    }, [imageUrl]);

    return <View style={styles.floorSection}>
        <VisitNavigation
            route={route}
            currentIndex={currentIndex}
            routeStarted={routeStarted}
            nextFloorTitle={nextFloorTitle}
            isLastRoute={isLastRoute}
            findifyDataContext={findifyDataContext}
            onPrevious={() => onPreviousPoint(routeId)}
            onNext={() => onNextPoint(routeId)}
            onRestart={() => onRestartRoute(routeId)}
        />
        <Pressable
            style={[
                styles.floorImageCard,
                isTablet ? styles.floorImageCardTablet : null,
                isLandscapeTablet ? styles.floorImageCardLandscapeTablet : null,
                { borderColor: colors.primary.withAlpha(45), backgroundColor: colors.background.solidAlphaWhiteBase(18) }
            ]}
            onPress={() => {
                if (shouldShowMap) {
                    setZoomOpen(true);
                    return;
                }
                onOpenPoint(currentPoint);
            }}>
            {imageUrl ? (
                <View
                    style={[
                        styles.floorImage,
                        isTablet ? styles.floorImageTablet : null,
                        isLandscapeTablet ? styles.floorImageLandscapeTablet : null,
                    ]}
                    onLayout={({ nativeEvent }) => setImageLayout(nativeEvent.layout)}>
                    <Image
                        source={{ uri: imageUrl }}
                        resizeMode="cover"
                        // react-native-web does not always expose source dimensions
                        // in this event; Image.getSize above remains the fallback.
                        onLoad={({ nativeEvent }: any) => {
                            if (nativeEvent?.source?.width && nativeEvent?.source?.height) {
                                setImageSourceSize(nativeEvent.source);
                            }
                        }}
                        style={styles.floorImageFill}
                    />
                    {renderMapMarkers(imageLayout, imageScale, "map")}
                </View>
            ) : null}
            {routeStarted && pointImageUrl ? (
                <Pressable style={[styles.mapOverlayButton, { backgroundColor: colors.primary.withAlpha(235) }]} onPress={(event: any) => {
                    event?.stopPropagation?.();
                    onOpenMap(currentPoint);
                }}>
                    <Icon name={shouldShowMap ? "image-outline" : "map-outline"} size={18} color="#fff" />
                    <PlaceText style={styles.mapOverlayText}>{shouldShowMap ? localizedText(findifyDataContext, "Foto", "Photo", "Photo") : localizedText(findifyDataContext, "Mapa", "Map", "Plan")}</PlaceText>
                </Pressable>
            ) : null}
        </Pressable>
        <View style={[styles.pointInfoCard, { backgroundColor: colors.background.solidAlphaWhiteBase(68), borderColor: colors.primary.withAlpha(25) }]}>
            <View style={styles.pointInfoHeader}>
                <PlaceText style={[styles.pointInfoEyebrow, { color: colors.primary.get() }]}>
                    {routeStarted ? localizedText(findifyDataContext, "LOCALIZAÇÃO ATUAL", "CURRENT LOCATION", "EMPLACEMENT ACTUEL") : localizedText(findifyDataContext, "PLANTA DO PISO", "FLOOR MAP", "PLAN DE L'ÉTAGE")}
                </PlaceText>
                {routeStarted && point ? (
                    <Pressable style={styles.pointInfoOpenButton} onPress={() => onOpenPoint(currentPoint)}>
                        <PlaceText style={[styles.pointInfoOpenText, { color: colors.primary.get() }]}>{localizedText(findifyDataContext, "Abrir detalhe", "Open details", "Ouvrir les détails")}</PlaceText>
                        <Icon name="open-outline" size={17} color={colors.primary.get()} />
                    </Pressable>
                ) : null}
            </View>
            <PlaceText style={[styles.pointInfoTitle, { color: colors.primary.get() }]}>
                {routeStarted && pointTitle ? pointTitle : getFloorTitle(category, findifyDataContext)}
            </PlaceText>
            {routeStarted ? <SimpleAudioPlayer audioUrl={audioUrl} findifyDataContext={findifyDataContext} title={localizedText(findifyDataContext, "Audioguia", "Audio Guide", "Audioguide")} /> : null}
            {!!pointSummary && routeStarted ? (
                <PlaceText numberOfLines={5} style={[styles.pointInfoSummary, { color: colors.text.get() }]}>{pointSummary}</PlaceText>
            ) : (
                <PlaceText style={[styles.pointInfoSummary, { color: colors.text.withAlpha(175) }]}>
                    {localizedText(findifyDataContext, "Use os botões acima para iniciar e seguir o percurso recomendado.", "Use the buttons above to start and follow the recommended route.", "Utilisez les boutons ci-dessus pour commencer et suivre le parcours recommandé.")}
                </PlaceText>
            )}
        </View>
        <Modal visible={zoomOpen} transparent={true} animationType="fade" onRequestClose={() => setZoomOpen(false)}>
            <View style={styles.zoomBackdrop}>
                <Pressable style={styles.zoomClose} onPress={() => setZoomOpen(false)}>
                    <Icon name="close-outline" size={34} color="#fff" />
                </Pressable>
                <ScrollView
                    horizontal
                    maximumZoomScale={4}
                    minimumZoomScale={1}
                    showsHorizontalScrollIndicator={false}
                    showsVerticalScrollIndicator={false}
                    contentContainerStyle={styles.zoomScrollContent}>
                    <ScrollView
                        maximumZoomScale={4}
                        minimumZoomScale={1}
                        showsHorizontalScrollIndicator={false}
                        showsVerticalScrollIndicator={false}
                        contentContainerStyle={styles.zoomScrollContent}>
                        {imageUrl ? <View style={styles.zoomImage} onLayout={({ nativeEvent }) => setZoomImageLayout(nativeEvent.layout)}>
                            <Image source={{ uri: imageUrl }} resizeMode="contain" style={styles.floorImageFill} />
                            {renderMapMarkers(zoomImageLayout, zoomImageScale, "zoom-map", true)}
                        </View> : null}
                    </ScrollView>
                </ScrollView>
            </View>
        </Modal>
    </View>
}

const FloorSelector = ({
    options,
    activeOptionKey,
    findifyDataContext,
    onSelectOption,
}: {
    options: any[],
    activeOptionKey?: string,
    findifyDataContext: FindifyDataContextType | null,
    onSelectOption: (option: any) => void,
}) => {
    const colors = PlaceColors(findifyDataContext);
    const [open, setOpen] = useState(false);
    const activeOption = options.find((option: any) => option.key === activeOptionKey);

    return <View style={styles.floorSelectorWrap}>
        <Pressable
            style={[styles.floorSelectBox, { borderColor: colors.primary.withAlpha(36), backgroundColor: colors.background.solidAlphaWhiteBase(60) }]}
            onPress={() => setOpen(true)}>
            <PlaceText style={[styles.floorSelectValue, { color: colors.primary.get() }]}>
                {activeOption?.label || localizedText(findifyDataContext, "Escolher piso", "Choose floor", "Choisir l'étage")}
            </PlaceText>
            <Icon name="chevron-down-outline" size={22} color={colors.primary.get()} />
        </Pressable>
        <Modal visible={open} transparent={true} animationType="fade" onRequestClose={() => setOpen(false)}>
            <Pressable style={styles.floorSelectBackdrop} onPress={() => setOpen(false)}>
                <View style={[styles.floorSelectModal, { backgroundColor: colors.background.get(), borderColor: colors.primary.withAlpha(35) }]}>
                    <View style={styles.floorSelectHandle} />
                    <PlaceText style={[styles.floorSelectTitle, { color: colors.primary.get() }]}>{localizedText(findifyDataContext, "Escolher piso", "Choose floor", "Choisir l'étage")}</PlaceText>
                    {options.map((option: any) => {
                        const isActive = option.key === activeOptionKey;
                        return <Pressable
                            key={"floor_selector_" + option.key}
                            style={[styles.floorSelectOption, { borderColor: colors.primary.withAlpha(25) }, isActive ? { backgroundColor: colors.primary.get() } : null]}
                            onPress={() => {
                                onSelectOption(option);
                                setOpen(false);
                            }}>
                            <PlaceText style={[styles.floorSelectOptionText, { color: colors.primary.get() }, isActive ? styles.floorSelectOptionTextActive : null]}>
                                {option.label}
                            </PlaceText>
                            {isActive ? <Icon name="checkmark-outline" size={22} color="#fff" /> : null}
                        </Pressable>
                    })}
                </View>
            </Pressable>
        </Modal>
    </View>
}

export const PlantsPage = ({ navigation, ...props }: any) => {
    const findifyDataContext = useContext(FindifyDataContext);
    useSeoTitle(localizedText(findifyDataContext, "Visita Guiada", "Guided Visit", "Visite guidée"), getWorkspaceTitle(findifyDataContext));
    const { width, height } = useWindowDimensions();
    const isTablet = width >= 768;
    const isLandscapeTablet = isTablet && width > height;
    const colors = PlaceColors(findifyDataContext);
    const [categoryTree, setCategoryTree] = useState<any[]>([]);
    const [routesById, setRoutesById] = useState<Record<number, any>>({});
    const [routeIds, setRouteIds] = useState<number[]>([]);
    const [currentPointIndexes, setCurrentPointIndexes] = useState<Record<number, number>>({});
    const [routeStartedById, setRouteStartedById] = useState<Record<number, boolean>>({});
    const [showMapByRouteId, setShowMapByRouteId] = useState<Record<number, boolean>>({});
    const [activeRouteId, setActiveRouteId] = useState<number | undefined>();

    useEffect(() => {
        let isMounted = true;
        const categoriesApi = getApiFromModule("categories", findifyDataContext as FindifyDataContextType);
        const routesApi = getApiFromModule("routes", findifyDataContext as FindifyDataContextType);

        Promise.all([
            categoriesApi?.getVisitTree?.("points_of_interest").catch(() =>
                categoriesApi?.getList(0, 100, null, { category_type: "points_of_interest" }, null)
            ),
            routesApi?.getList(0, 100, null, {}, null),
        ]).then(async ([categoriesRes, routesRes]: any[]) => {
            const nextCategoryTree = (categoriesRes?.results ?? [])
                .map((category: any) => ({ ...category, __floorMeta: getFloorMeta(category) }))
                .filter((category: any) => category.active !== false)
                .sort((a: any, b: any) => {
                    if (a.__floorMeta && b.__floorMeta) return a.__floorMeta.order - b.__floorMeta.order;
                    return Number(a.position ?? 0) - Number(b.position ?? b.category_id ?? 0);
                });

            const routeSummaries = (routesRes?.results ?? [])
                .filter(isPublishedRoute)
                .sort((a: any, b: any) => Number(a.position ?? a.route_id ?? 0) - Number(b.position ?? b.route_id ?? 0));

            const routeDetails = await Promise.all(routeSummaries.map((route: any) => {
                const routeLookup = route?.route_id ?? route?.page_slug;
                return routesApi?.get(routeLookup, null, null)
                    .then((details: any) => ({ ...details, __routeSummary: route }))
                    .catch(() => null);
            }));

            if (!isMounted) return;

            const publishedRouteDetails = routeDetails.filter((route: any) => route && isPublishedRoute(route));
            const nextRoutes: Record<number, any> = {};
            publishedRouteDetails.forEach((route: any) => {
                nextRoutes[route.route_id] = route;
            });
            const nextRouteIds = publishedRouteDetails.map((route: any) => route.route_id);

            setCategoryTree(nextCategoryTree);
            setRoutesById(nextRoutes);
            setRouteIds(nextRouteIds);
            const targetCategoryId = Number(props?.route?.params?.category_id);
            const targetRoute = targetCategoryId
                ? publishedRouteDetails.find((route: any) => getRoutePoints(route).some((routePoint: any) => (
                    routePoint?.point_of_interest?.categories?.some((line: any) => Number(line?.category_id ?? line?.category?.category_id) === targetCategoryId)
                )))
                : undefined;
            const targetRouteId = targetRoute?.route_id;
            const targetPointIndex = targetRouteId
                ? getRoutePoints(targetRoute).findIndex((routePoint: any) => (
                    routePoint?.point_of_interest?.categories?.some((line: any) => Number(line?.category_id ?? line?.category?.category_id) === targetCategoryId)
                ))
                : 0;
            setActiveRouteId(targetRouteId ?? nextRouteIds[0]);
            setRouteStartedById(nextRouteIds.reduce((started, routeId) => ({
                ...started,
                [routeId]: true,
            }), {} as Record<number, boolean>));
            setShowMapByRouteId({});
            setCurrentPointIndexes((current) => {
                const next = { ...current };
                nextRouteIds.forEach((routeId) => {
                    next[routeId] = next[routeId] ?? 0;
                });
                if (targetRouteId && targetPointIndex >= 0) next[targetRouteId] = targetPointIndex;
                return next;
            });
        }).catch((error: any) => {
            console.error("Failed to load plants floors/routes:", error);
        });

        return () => {
            isMounted = false;
        }
    }, [findifyDataContext?.workspace?.workspace_id, findifyDataContext?.visitVersion, props?.route?.params?.category_id]);

    const categoryForRoute = (route: any) => {
        const routeId = route?.route_id;
        const pointIndex = routeStartedById[routeId] ? (currentPointIndexes[routeId] ?? 0) : 0;
        return findLocationCategory(getRoutePoints(route)[pointIndex]?.point_of_interest, categoryTree)
            ?? findFloorCategoryForRoute(route, categoryTree);
    };
    const activeRoute = activeRouteId ? routesById[activeRouteId] : undefined;
    const activeCategory = activeRoute ? categoryForRoute(activeRoute) : undefined;

    const changeRoutePoint = (routeId: number, direction: -1 | 1) => {
        const points = getRoutePoints(routesById[routeId]);
        setActiveRouteId(routeId);

        if (direction === 1 && !routeStartedById[routeId]) {
            setRouteStartedById((current) => ({
                ...current,
                [routeId]: true,
            }));
            setShowMapByRouteId((current) => ({
                ...current,
                [routeId]: false,
            }));
            return;
        }

        setCurrentPointIndexes((current) => {
            const currentIndex = current[routeId] ?? 0;
            if (direction === -1 && currentIndex === 0) {
                return current;
            }

            const nextIndex = Math.max(0, Math.min(points.length - 1, currentIndex + direction));
            return {
                ...current,
                [routeId]: nextIndex,
            };
        });
    }

    const restartVisit = () => {
        if (!activeRouteId) return;
        setCurrentPointIndexes((current) => ({ ...current, [activeRouteId]: 0 }));
        setRouteStartedById((current) => ({ ...current, [activeRouteId]: true }));
        setShowMapByRouteId((current) => ({ ...current, [activeRouteId]: false }));
    }

    const openRoutePointDetails = (routePoint: any, openMap = false) => {
        const point = routePoint?.point_of_interest;
        if (!point?.point_of_interest_id) return;
        const infoCategory = getInfoPointCategory(routePoint);
        if (infoCategory?.category_id) {
            navigation.push("PlaceContentList", {
                module: "points_of_interest",
                category: infoCategory.category_id,
                ...(openMap ? {
                    ...(isCategoryBasePoint(routePoint) ? { base_content_id: point.point_of_interest_id } : {}),
                    open_map: true,
                } : { base_content_id: point.point_of_interest_id }),
            });
            return;
        }
        if (openMap) {
            return;
        }
        navigation.push("PlaceContentDetails", {
            module: "points_of_interest",
            content_id: point.point_of_interest_id,
            page_slug: point.page_slug,
        });
    }

    const titleSection = <View style={[styles.titleBlock, isLandscapeTablet ? styles.titleBlockLandscapeTablet : null]}>
        <PlaceText style={[styles.title, { color: colors.primary.get() }]}>{localizedText(findifyDataContext, "Visita Guiada", "Guided Visit", "Visite guidée")}</PlaceText>
        <PlaceText style={[styles.subtitle, { color: colors.text.withAlpha(210) }]}>{localizedText(findifyDataContext, "Percurso recomendado", "Recommended route", "Parcours recommandé")}</PlaceText>
    </View>;

    const routeSelectorOptions = routeIds
        .map((routeId) => routesById[routeId])
        .filter(Boolean)
        .flatMap((route: any) => {
            const routeTitle = getTranslatedString(route?.title, findifyDataContext as FindifyDataContextType);
            const categoriesSeen = new Set<number>();
            const categoryOptions = getRoutePoints(route).flatMap((routePoint: any, pointIndex: number) => {
                const category = findLocationCategory(routePoint?.point_of_interest, categoryTree)
                    ?? findFloorCategoryForRoute(route, categoryTree);
                if (!category?.category_id || categoriesSeen.has(category.category_id)) return [];
                categoriesSeen.add(category.category_id);
                return [{
                    key: `${route.route_id}-${category.category_id}`,
                    routeId: route.route_id,
                    pointIndex,
                    categoryId: category.category_id,
                    label: getFloorTitle(category, findifyDataContext) || routeTitle,
                }];
            });
            return categoryOptions.length > 0
                ? categoryOptions
                : [{ key: `${route.route_id}`, routeId: route.route_id, pointIndex: 0, label: routeTitle }];
        });
    const activeOptionKey = activeRoute
        ? activeCategory?.category_id ? `${activeRoute.route_id}-${activeCategory.category_id}` : `${activeRoute.route_id}`
        : undefined;

    const floorSelectorSection = <FloorSelector
        options={routeSelectorOptions}
        activeOptionKey={activeOptionKey}
        findifyDataContext={findifyDataContext}
        onSelectOption={(option: any) => {
            setActiveRouteId(option.routeId);
            setCurrentPointIndexes((current) => ({ ...current, [option.routeId]: option.pointIndex }));
            setRouteStartedById((current) => ({ ...current, [option.routeId]: true }));
            setShowMapByRouteId((current) => ({ ...current, [option.routeId]: false }));
        }}
    />;

    const floorCardSection = activeCategory && activeRoute ? (
        <View style={isLandscapeTablet ? styles.tabletLandscapeMapWrap : undefined}>
            <FloorCard
                key={`${activeRoute.route_id}-${activeCategory.category_id}`}
                category={activeCategory}
                route={activeRoute}
                currentIndex={currentPointIndexes[activeRoute.route_id] ?? 0}
                routeStarted={routeStartedById[activeRoute.route_id] ?? false}
                showMap={showMapByRouteId[activeRoute.route_id] ?? false}
                activeRouteId={activeRouteId ?? activeRoute.route_id}
                routeId={activeRoute.route_id}
                isLastRoute={true}
                findifyDataContext={findifyDataContext}
                onPreviousPoint={(routeId: number) => changeRoutePoint(routeId, -1)}
                onNextPoint={(routeId: number) => changeRoutePoint(routeId, 1)}
                onRestartRoute={restartVisit}
                onOpenPoint={openRoutePointDetails}
                onOpenMap={(routePoint: any) => openRoutePointDetails(routePoint, true)}
                onToggleMap={(routeId: number) => setShowMapByRouteId((current) => ({
                    ...current,
                    [routeId]: !current[routeId],
                }))}
                isTablet={isTablet}
                isLandscapeTablet={isLandscapeTablet}
            />
        </View>
    ) : null;

    return <BasePage navigation={navigation}>
        <View style={[styles.page, { backgroundColor: colors.background.get() }]}>
            <BiscainhosAppHeader findifyDataContext={findifyDataContext} navigation={navigation} />
            <ScrollView
                bounces={false}
                showsVerticalScrollIndicator={false}
                contentContainerStyle={[
                    styles.content,
                    {
                        maxWidth: isLandscapeTablet ? 1180 : isTablet ? 820 : 960,
                        paddingHorizontal: isTablet ? 40 : 24,
                    }
                ]}>
                {isLandscapeTablet ? (
                    <View style={styles.tabletLandscapeLayout}>
                        <View style={styles.tabletLandscapeSidebar}>
                            {titleSection}
                            {floorSelectorSection}
                        </View>
                        <View style={styles.tabletLandscapeMapPane}>
                            {floorCardSection}
                        </View>
                    </View>
                ) : (
                    <>
                        {titleSection}
                        {floorSelectorSection}
                        {floorCardSection}
                    </>
                )}
            </ScrollView>
        </View>
    </BasePage>
}

const styles = StyleSheet.create({
    page: {
        flex: 1,
        backgroundColor: "#f9f7f2",
    },
    content: {
        paddingTop: 22,
        paddingBottom: BottomNavigationPadding + 42,
        width: "100%" as any,
        alignSelf: "center",
    },
    tabletLandscapeMapWrap: {
        width: '100%',
        alignSelf: 'center',
    },
    tabletLandscapeLayout: {
        width: "100%" as any,
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 28,
    },
    tabletLandscapeSidebar: {
        width: 340,
        gap: 0,
    },
    tabletLandscapeMapPane: {
        flex: 1,
        minWidth: 0,
    },
    titleBlock: {
        marginBottom: 16,
    },
    titleBlockLandscapeTablet: {
        marginBottom: 22,
    },
    title: {
        color: "#990000",
        fontFamily: "Georgia",
        fontSize: 32,
        lineHeight: 40,
        fontWeight: "700",
    },
    subtitle: {
        color: "#3a2a28",
        fontSize: 18,
        lineHeight: 28,
        marginTop: 4,
    },
    currentLocationBox: {
        borderWidth: 1,
        borderColor: "rgba(153, 0, 0, 0.28)",
        borderRadius: 8,
        paddingHorizontal: 10,
        paddingVertical: 10,
        marginBottom: 16,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
    },
    currentLocationBoxLandscapeTablet: {
        flexDirection: "column",
        alignItems: "stretch",
        paddingHorizontal: 14,
        paddingVertical: 14,
        marginBottom: 18,
    },
    currentLocationInfo: {
        flex: 1,
        minWidth: 0,
    },
    roomPill: {
        alignSelf: "flex-start",
        backgroundColor: "#f5c443",
        borderRadius: 14,
        paddingHorizontal: 12,
        paddingVertical: 4,
        marginBottom: 7,
        shadowOpacity: 0.22,
        shadowRadius: 10,
        shadowOffset: { width: 0, height: 0 },
    },
    roomPillText: {
        color: "#6b4a10",
        fontSize: 14,
        lineHeight: 20,
        fontWeight: "700",
    },
    currentLocationText: {
        color: "#990000",
        fontSize: 14,
        lineHeight: 20,
        fontWeight: "700",
        letterSpacing: 2,
    },
    currentNavActions: {
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
        borderLeftWidth: 1,
        paddingLeft: 8,
    },
    currentNavActionsLandscapeTablet: {
        borderLeftWidth: 0,
        borderTopWidth: 1,
        paddingLeft: 0,
        paddingTop: 10,
        justifyContent: "space-between",
    },
    currentNavButton: {
        minWidth: 52,
        minHeight: 44,
        alignItems: "center",
        justifyContent: "center",
        paddingHorizontal: 4,
        paddingVertical: 2,
        borderRadius: 6,
    },
    currentNavButtonLandscapeTablet: {
        flex: 1,
        minHeight: 52,
        backgroundColor: "rgba(255,255,255,0.44)",
    },
    currentNavText: {
        marginTop: 1,
        fontSize: 8,
        lineHeight: 10,
        fontWeight: "700",
        letterSpacing: 0.2,
        textTransform: "uppercase",
        textAlign: "center",
    },
    floorSelectorWrap: {
        alignItems: "stretch",
        marginBottom: 20,
        width: "100%" as any,
    },
    floorSelectBox: {
        minHeight: 54,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        borderWidth: 1,
        borderRadius: 8,
        paddingHorizontal: 16,
        paddingVertical: 10,
        shadowColor: "#000",
        shadowOpacity: 0.07,
        shadowRadius: 6,
        shadowOffset: { width: 0, height: 2 },
    },
    floorSelectValue: {
        fontSize: 18,
        lineHeight: 24,
        fontWeight: "700",
    },
    floorSelectBackdrop: {
        flex: 1,
        backgroundColor: "rgba(0,0,0,0.42)",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
    },
    floorSelectModal: {
        width: "100%" as any,
        maxWidth: 420,
        borderWidth: 1,
        borderRadius: 12,
        padding: 18,
        shadowColor: "#000",
        shadowOpacity: 0.2,
        shadowRadius: 16,
        shadowOffset: { width: 0, height: 8 },
    },
    floorSelectHandle: {
        width: 46,
        height: 4,
        borderRadius: 2,
        alignSelf: "center",
        backgroundColor: "rgba(153,0,0,0.22)",
        marginBottom: 14,
    },
    floorSelectTitle: {
        fontFamily: "Georgia",
        fontSize: 24,
        lineHeight: 30,
        fontWeight: "700",
        marginBottom: 14,
    },
    floorSelectOption: {
        minHeight: 54,
        borderWidth: 1,
        borderRadius: 8,
        paddingHorizontal: 14,
        paddingVertical: 10,
        marginBottom: 10,
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        backgroundColor: "rgba(255,255,255,0.55)",
    },
    floorSelectOptionText: {
        fontSize: 17,
        lineHeight: 23,
        fontWeight: "700",
    },
    floorSelectOptionTextActive: {
        color: "#fff",
    },
    floorSection: {
        marginTop: 0,
        marginBottom: 34,
    },
    floorTitleRow: {
        flexDirection: "row",
        alignItems: "center",
        marginBottom: 14,
        gap: 16,
    },
    floorTitleLine: {
        flex: 1,
        height: 1,
        backgroundColor: "rgba(226, 177, 60, 0.35)",
    },
    floorTitle: {
        color: "#990000",
        fontFamily: "Georgia",
        fontSize: 26,
        lineHeight: 34,
        fontWeight: "700",
        textAlign: "center",
    },
    floorImageCard: {
        minHeight: 360,
        borderWidth: 1,
        borderColor: "rgba(153, 0, 0, 0.24)",
        borderRadius: 8,
        backgroundColor: "#fff",
        shadowColor: "#000",
        shadowOpacity: 0.12,
        shadowRadius: 10,
        shadowOffset: { width: 0, height: 3 },
        alignItems: "center",
        justifyContent: "center",
        padding: 8,
        overflow: "hidden",
        marginBottom: 14,
    },
    floorImageCardTablet: {
        minHeight: 520,
    },
    floorImageCardLandscapeTablet: {
        minHeight: 500,
    },
    floorImage: {
        width: "100%",
        height: 344,
        overflow: "hidden",
        position: "relative",
    },
    floorImageFill: {
        width: "100%",
        height: "100%",
    },
    floorImageTablet: {
        height: 504,
    },
    floorImageLandscapeTablet: {
        height: 484,
    },
    mapPointMarker: {
        position: "absolute",
        zIndex: 10,
        elevation: 10,
        width: 42,
        height: 42,
        alignItems: "center",
        justifyContent: "center",
    },
    mapOverlayButton: {
        position: "absolute",
        top: 14,
        right: 14,
        minWidth: 74,
        height: 40,
        borderRadius: 20,
        backgroundColor: "rgba(153, 0, 0, 0.92)",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        paddingHorizontal: 12,
        shadowColor: "#000",
        shadowOpacity: 0.22,
        shadowRadius: 7,
        shadowOffset: { width: 0, height: 3 },
        zIndex: 2,
        backdropFilter: "blur(8px)" as any,
    },
    mapOverlayText: {
        color: "#fff",
        fontSize: 11,
        lineHeight: 14,
        fontWeight: "700",
        letterSpacing: 1,
        textTransform: "uppercase",
    },
    visitNavigation: {
        flexDirection: "row",
        gap: 10,
    },
    visitNavigationWrap: {
        marginBottom: 14,
        gap: 10,
    },
    visitNavButton: {
        flex: 1,
        minHeight: 74,
        borderRadius: 8,
        borderWidth: 1,
        backgroundColor: "rgba(255,255,255,0.72)",
        paddingHorizontal: 12,
        paddingVertical: 10,
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        shadowColor: "#000",
        shadowOpacity: 0.08,
        shadowRadius: 8,
        shadowOffset: { width: 0, height: 2 },
    },
    visitNavButtonFullWidth: {
        flex: undefined,
        width: "100%",
    },
    visitNavButtonNext: {
        justifyContent: "space-between",
    },
    visitNavDisabled: {
        opacity: 0.55,
    },
    visitCompleteCard: {
        borderWidth: 1,
        borderRadius: 10,
        padding: 16,
        gap: 12,
        shadowColor: "#000",
        shadowOpacity: 0.18,
        shadowRadius: 8,
        shadowOffset: { width: 0, height: 4 },
    },
    visitCompleteIcon: {
        width: 46,
        height: 46,
        borderRadius: 23,
        alignItems: "center",
        justifyContent: "center",
    },
    visitCompleteCopy: {
        marginTop: -58,
        marginLeft: 58,
    },
    visitCompleteTitle: {
        color: "#fff",
        fontFamily: "Georgia",
        fontSize: 22,
        lineHeight: 27,
        fontWeight: "700",
    },
    visitCompleteText: {
        marginTop: 3,
        color: "rgba(255,255,255,0.85)",
        fontSize: 14,
        lineHeight: 19,
    },
    visitRestartButton: {
        minHeight: 42,
        marginTop: 4,
        borderRadius: 7,
        borderWidth: 2,
        borderColor: "rgba(255,255,255,0.92)",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
    },
    visitRestartButtonText: {
        color: "#fff",
        fontSize: 15,
        fontWeight: "800",
        textTransform: "uppercase",
        letterSpacing: 0.7,
    },
    visitNavTextBlock: {
        flex: 1,
        minWidth: 0,
    },
    visitNavLabel: {
        fontSize: 11,
        lineHeight: 15,
        fontWeight: "700",
        textTransform: "uppercase",
        letterSpacing: 1.1,
    },
    visitNavTitle: {
        marginTop: 4,
        fontSize: 14,
        lineHeight: 18,
        fontWeight: "700",
    },
    visitNavLabelNext: {
        color: "rgba(255,255,255,0.75)",
        fontSize: 11,
        lineHeight: 15,
        fontWeight: "700",
        textTransform: "uppercase",
        letterSpacing: 1.1,
    },
    visitNavTitleNext: {
        color: "#fff",
        marginTop: 4,
        fontSize: 14,
        lineHeight: 18,
        fontWeight: "700",
    },
    pointInfoCard: {
        borderWidth: 1,
        borderRadius: 8,
        padding: 16,
        marginBottom: 22,
        shadowColor: "#000",
        shadowOpacity: 0.06,
        shadowRadius: 8,
        shadowOffset: { width: 0, height: 2 },
    },
    pointInfoHeader: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 10,
        marginBottom: 8,
    },
    pointInfoEyebrow: {
        fontSize: 12,
        lineHeight: 16,
        fontWeight: "700",
        letterSpacing: 1.8,
    },
    pointInfoOpenButton: {
        minHeight: 32,
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
    },
    pointInfoOpenText: {
        fontSize: 12,
        lineHeight: 16,
        fontWeight: "700",
    },
    pointInfoTitle: {
        fontFamily: "Georgia",
        fontSize: 26,
        lineHeight: 32,
        fontWeight: "700",
        marginBottom: 8,
    },
    pointInfoSummary: {
        fontSize: 16,
        lineHeight: 25,
    },
    zoomBackdrop: {
        flex: 1,
        backgroundColor: "rgba(0, 0, 0, 0.92)",
        alignItems: "center",
        justifyContent: "center",
    },
    zoomClose: {
        position: "absolute",
        top: 18,
        right: 18,
        zIndex: 2,
        width: 48,
        height: 48,
        borderRadius: 24,
        backgroundColor: "rgba(153, 0, 0, 0.9)",
        alignItems: "center",
        justifyContent: "center",
    },
    zoomScrollContent: {
        minWidth: "100%" as any,
        minHeight: "100%" as any,
        alignItems: "center",
        justifyContent: "center",
    },
    zoomImage: {
        width: "100vw" as any,
        height: "90vh" as any,
    },
});
